import { z } from "zod";
import { ImplementationMetaSchema } from "./implementations.js";

/**
 * Normalized app item returned by listApps endpoint.
 * This extends ImplementationMetaSchema with transformed fields (title, key, implementation_id).
 */
export const AppItemSchema = ImplementationMetaSchema.omit({
  name: true,
  id: true,
}).extend({
  title: z.string().describe("Display name of the app"),
  key: z.string().describe("App key (versionless implementation name)"),
  implementation_id: z
    .string()
    .describe("Full implementation ID including version"),
  version: z.string().optional().describe("App version"),
});

export type AppItem = z.infer<typeof AppItemSchema>;

/**
 * Query parameters for listing apps
 */
export const ListAppsQuerySchema = z
  .object({
    /** @deprecated Use app_keys instead */
    appKeys: z
      .string()
      .optional()
      .describe(
        "Deprecated: Use app_keys instead. Comma-separated list of app keys to filter by (e.g., 'SlackCLIAPI' or slugs like 'github,slack')",
      ),
    app_keys: z
      .string()
      .optional()
      .describe(
        "Comma-separated list of app keys to filter by (e.g., 'SlackCLIAPI' or slugs like 'github,slack')",
      ),
    search: z
      .string()
      .optional()
      .describe("Search term to filter apps by name"),
    /** @deprecated Use page_size instead */
    pageSize: z
      .number()
      .min(1)
      .optional()
      .describe("Deprecated: Use page_size instead. Number of apps per page"),
    page_size: z.number().min(1).optional().describe("Number of apps per page"),
    offset: z
      .string()
      .optional()
      .describe("Pagination offset from previous response"),
  })
  .describe("Query parameters for listing apps");

/**
 * Response schema for listApps
 */
export const ListAppsResponseSchema = z.object({
  data: z.array(AppItemSchema).describe("Array of app items"),
  links: z
    .object({
      next: z
        .string()
        .nullable()
        .optional()
        .describe(
          "Fully qualified URL for the next page of results (if available)",
        ),
    })
    .describe("Pagination links for navigating through results"),
  meta: z
    .object({
      count: z.number().describe("Number of items in current page"),
      limit: z.number().describe("Number of items per page"),
      offset: z.number().describe("Offset of the current page"),
    })
    .describe("Metadata for the paginated result set"),
});

export type ListAppsQuery = z.infer<typeof ListAppsQuerySchema>;
export type ListAppsResponse = z.infer<typeof ListAppsResponseSchema>;
